// ═══════════════════════════════════════════════════════════════
// ImpactPortalReports — Recent assessments list + detail drawer
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useCallback } from 'react';
import { IconMapPin, IconX, IconChevronLeft } from '@tabler/icons-react';
import { useAuthStore } from '../../../store/authStore';
import { useImpactStore } from '../../../store/impactStore';
import type { Assessment, AssessmentPhoto } from '../../../store/impactStore';
import '../../shelter/portal/ShelterPortal.css';
import './ImpactPortal.css';

// ── Helpers ───────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  affected:       '#16a34a',
  minor:          '#d97706',
  major:          '#ea580c',
  destroyed:      '#B12525',
  inaccessible:   '#7c3aed',
  structural:     '#4B3FFF',
  infrastructure: '#0369a1',
  vehicle:        '#b45309',
  utility:        '#0f766e',
  other:          '#7B7D7F',
};

function categoryColor(cat: string): string {
  return CATEGORY_COLORS[cat?.toLowerCase()] || '#939598';
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return dateStr; }
}

function statusClass(status: string): string {
  const s = status?.toLowerCase() || 'draft';
  if (s === 'submitted') return 'submitted';
  if (s === 'review')    return 'review';
  if (s === 'approved')  return 'approved';
  return 'draft';
}

function cap(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
}

// ── Detail drawer ─────────────────────────────────────────────

interface DetailDrawerProps {
  assessment: Assessment;
  onClose: () => void;
}

function DetailDrawer({ assessment: a, onClose }: DetailDrawerProps) {
  const { loadPhotos } = useImpactStore();
  const [photos, setPhotos] = useState<AssessmentPhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  useEffect(() => {
    setPhotosLoading(true);
    loadPhotos(a.id).then(p => {
      setPhotos(p);
      setPhotosLoading(false);
    });
  }, [a.id, loadPhotos]);

  const dotColor = categoryColor(a.damageCategory);
  const typeLabel = a.assessmentType === 'public' ? 'PA — Public' : 'IA — Individual';

  const detailRows: { label: string; value: string | number | null | undefined }[] = [
    { label: 'Category',   value: cap(a.damageCategory) },
    { label: 'Type',       value: typeLabel },
    { label: 'Status',     value: cap(a.status) },
    { label: 'Method',     value: a.method ? cap(a.method) : null },
    { label: 'Assessor',   value: a.assessorName || null },
    { label: 'Email',      value: a.assessorEmail || null },
    { label: 'Date',       value: formatDate(a.datecreated || '') },
    { label: 'Homes',      value: (a.homeCount && a.homeCount > 1) ? a.homeCount : null },
    { label: 'Facility',   value: (a.facilityName as string) || null },
    { label: 'PA Cat',     value: (a.paCategory as string) || null },
    { label: 'Occupancy',  value: a.occupancyType || null },
    { label: 'Structure',  value: a.structureType || null },
    { label: 'Est. Loss',  value: a.estimatedLoss ? `$${a.estimatedLoss.toLocaleString()}` : null },
    { label: 'Severity',   value: a.severity != null ? String(a.severity) : null },
    { label: 'City',       value: a.city || null },
    { label: 'County',     value: a.county || null },
    { label: 'State',      value: a.state || null },
    { label: 'ZIP',        value: a.zip || null },
  ].filter(r => r.value != null && r.value !== '');

  return (
    <>
      {/* Lightbox */}
      {lightboxSrc && (
        <div className="ip-lightbox" onClick={() => setLightboxSrc(null)}>
          <button className="ip-lightbox-close" onClick={() => setLightboxSrc(null)}>
            <IconX size={20} />
          </button>
          <img src={`data:image/jpeg;base64,${lightboxSrc}`} alt="Assessment photo" className="ip-lightbox-img" />
        </div>
      )}

      <div className="ip-detail-overlay">
        {/* Header */}
        <div className="ip-detail-header">
          <button className="ip-back-btn" onClick={onClose}>
            <IconChevronLeft size={16} />
            Back
          </button>
          <div className="ip-detail-title">Assessment #{a.id}</div>
        </div>

        <div className="ip-detail-body">
          {/* Address hero */}
          <div className="ip-detail-hero">
            <div className="ip-report-dot" style={{ background: dotColor, width: 14, height: 14, marginTop: 2, flexShrink: 0 }} />
            <div>
              <div className="ip-detail-addr">{a.address || 'No address'}</div>
              {(a.latitude && a.longitude) ? (
                <div style={{ fontSize: 12, color: '#7B7D7F', display: 'flex', gap: 4, alignItems: 'center', marginTop: 2 }}>
                  <IconMapPin size={12} />
                  {a.latitude.toFixed(5)}, {a.longitude.toFixed(5)}
                </div>
              ) : null}
            </div>
          </div>

          {/* Description */}
          {a.description && (
            <div className="ip-detail-section">
              <div className="ip-detail-section-label">Description</div>
              <div className="ip-detail-desc">{a.description}</div>
            </div>
          )}

          {/* Notes */}
          {a.notes && (() => {
            try {
              const parsed = JSON.parse(a.notes);
              const noteFields = Object.entries(parsed).filter(([, v]) => v);
              if (noteFields.length === 0) return null;
              return (
                <div className="ip-detail-section">
                  <div className="ip-detail-section-label">Additional Info</div>
                  {noteFields.map(([k, v]) => (
                    <div key={k} className="ip-detail-row">
                      <span className="ip-detail-row-label">{cap(k.replace(/([A-Z])/g, ' $1'))}</span>
                      <span className="ip-detail-row-val">{String(v)}</span>
                    </div>
                  ))}
                </div>
              );
            } catch { return null; }
          })()}

          {/* Detail rows */}
          <div className="ip-detail-section">
            <div className="ip-detail-section-label">Details</div>
            {detailRows.map(row => (
              <div key={row.label} className="ip-detail-row">
                <span className="ip-detail-row-label">{row.label}</span>
                <span className="ip-detail-row-val">{row.value}</span>
              </div>
            ))}
          </div>

          {/* Photos */}
          <div className="ip-detail-section">
            <div className="ip-detail-section-label">
              Photos {!photosLoading && `(${photos.length})`}
            </div>
            {photosLoading ? (
              <div style={{ fontSize: 14, color: '#939598', padding: '8px 0' }}>Loading photos…</div>
            ) : photos.length === 0 ? (
              <div style={{ fontSize: 14, color: '#939598', padding: '8px 0' }}>No photos attached</div>
            ) : (
              <div className="ip-detail-photos">
                {photos.map(p => (
                  <button
                    key={p.id}
                    className="ip-detail-photo-btn"
                    onClick={() => setLightboxSrc(p.photoUrl)}
                  >
                    <img
                      src={`data:image/jpeg;base64,${p.photoUrl}`}
                      alt={p.caption || `Photo ${p.id}`}
                    />
                    {p.caption && <span className="ip-detail-photo-cap">{p.caption}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Card ──────────────────────────────────────────────────────

function AssessmentCard({ a, onClick }: { a: Assessment; onClick: () => void }) {
  const dotColor = categoryColor(a.damageCategory);
  const typeLabel = a.assessmentType === 'public' ? 'PA' : 'IA';
  const catLabel = a.damageCategory ? cap(a.damageCategory) : 'Unknown';

  return (
    <button className="ip-report-card ip-report-card--tappable" onClick={onClick}>
      <div className="ip-report-dot" style={{ background: dotColor }} />
      <div className="ip-report-body">
        <div className="ip-report-addr">{a.address || 'No address'}</div>
        <div className="ip-report-chips">
          <span className="ip-cat-chip">{catLabel}</span>
          <span className="ip-cat-chip">{typeLabel}</span>
          <span className={`ip-status-chip ${statusClass(a.status)}`}>{a.status || 'draft'}</span>
          {(a.homeCount && a.homeCount > 1) ? <span className="ip-cat-chip">{a.homeCount} homes</span> : null}
        </div>
        <div className="ip-report-meta">
          {formatDate(a.datecreated || '')}
          {a.assessorName ? ` · ${a.assessorName}` : ''}
        </div>
      </div>
      <div className="ip-report-chevron">›</div>
    </button>
  );
}

// ── Main component ────────────────────────────────────────────

export default function ImpactPortalReports() {
  const { activeIncident } = useAuthStore();
  const { assessments, loadAssessments, loading } = useImpactStore();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Assessment | null>(null);

  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  const filtered = useMemo(() => {
    if (!activeIncident) return [];
    const q = search.trim().toLowerCase();
    return [...assessments]
      .filter(a => {
        if (String(a.incidentId) !== String(activeIncident.id)) return false;
        if (!q) return true;
        return (
          (a.address || '').toLowerCase().includes(q) ||
          (a.damageCategory || '').toLowerCase().includes(q) ||
          (a.assessorName || '').toLowerCase().includes(q) ||
          (a.status || '').toLowerCase().includes(q) ||
          (a.description || '').toLowerCase().includes(q) ||
          String(a.id).includes(q)
        );
      })
      .sort((a, b) => {
        const da = a.datecreated ? new Date(a.datecreated).getTime() : 0;
        const db = b.datecreated ? new Date(b.datecreated).getTime() : 0;
        return db - da;
      })
      .slice(0, 100);
  }, [assessments, activeIncident, search]);

  const handleClose = useCallback(() => setSelected(null), []);

  if (selected) {
    return <DetailDrawer assessment={selected} onClose={handleClose} />;
  }

  return (
    <div className="ip-reports-wrap">
      <div className="ip-reports-header">
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B1C1C' }}>Recent Assessments</div>
          <div style={{ fontSize: 12, color: '#7B7D7F' }}>
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
            {activeIncident ? ` · ${activeIncident.name}` : ''}
          </div>
        </div>
        <button className="ip-refresh-btn" onClick={loadAssessments} disabled={loading}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      {/* Search */}
      <div className="ip-search-wrap">
        <input
          className="sp-input ip-search-input"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search address, category, assessor…"
        />
        {search && (
          <button className="ip-search-clear" onClick={() => setSearch('')}>
            <IconX size={14} />
          </button>
        )}
      </div>

      {loading && filtered.length === 0 ? (
        <div className="ip-empty">
          <div className="ip-empty-sub">Loading assessments…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ip-empty">
          <div className="ip-empty-icon">📋</div>
          <div className="ip-empty-title">{search ? 'No matches' : 'No assessments yet'}</div>
          <div className="ip-empty-sub">
            {search ? 'Try a different search term.' : 'Assessments submitted for this incident will appear here.'}
          </div>
        </div>
      ) : (
        <div>
          {filtered.map(a => (
            <AssessmentCard key={a.id} a={a} onClick={() => setSelected(a)} />
          ))}
        </div>
      )}
    </div>
  );
}
